import React, { useContext, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageDropzone, FileUploadButton } from 'react-file-utils';
import { useApolloClient } from 'react-apollo';
import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { debounce } from 'lodash';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import classNames from 'classnames';
import * as Sentry from "@sentry/browser";
import {
  ChannelContext,
  ChatAutoComplete,
  // @ts-ignore
  useMessageInput,
  MessageInputProps,
  UserItemProps,
} from 'stream-chat-react';

import { ProfileSuggestion, UserSuggestDocument } from 'api';
import { useCurrentUser, useNotify } from 'hooks';

import { commandsDataProvider, emojiDataProvider } from './triggers';
import EmojiPicker from './EmojiPicker';
import UploadsPreview from './UploadsPreview';
import Suggestion from './Suggestion';
import useChatContext from './useChatContext';

const ACCEPTED_FILES = ['image/*'];

const useStyles = makeStyles(theme => ({
  root: {
    '@global': {
      '.rfu-file-upload-button': {
        top: 'calc(100% - 46px)',
      },
      '.str-chat__input-flat-emojiselect': {
        top: 'calc(100% - 47px)',
      },
    },
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const MessageInputCustom = (props: MessageInputProps) => {
  const classes = useStyles();
  const { channel } = useChatContext();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const apolloClient = useApolloClient();
  const messageInput = useMessageInput(props);
  const channelContext = useContext(ChannelContext);
  const notify = useNotify();
  const { SendButton } = props;

  const getCommands = useCallback(() => channel && channel.getConfig().commands, [channel]);
  const debouncedQuery = debounce(queryUsers, 200, {
    trailing: true,
    leading: false,
  });

  async function queryUsers(prefix: string, onReady: (users: ProfileSuggestion[]) => void) {
    const { data } = await apolloClient.query({
      query: UserSuggestDocument,
      variables: { prefix },
    });

    if (!data || !data.suggest) {
      return onReady([]);
    }

    const modifiedUsers = data.suggest.results.map((user: ProfileSuggestion) => ({
      name: user.title,
      ...user,
    }));

    onReady && onReady(modifiedUsers);
  }

  const getTriggers = useCallback(
    () => ({
      '@': {
        dataProvider: (query: string, text: string, onReady: (data: object, q: string) => void) => {
          return debouncedQuery(query, data => {
            onReady(data, query);
          });
        },
        component: Suggestion,
        output: (entity: UserItemProps['entity']) => ({
          key: entity.id,
          text: `@${entity.name || entity.id}`,
          caretPosition: 'next',
        }),
        callback: (item: UserItemProps['entity']) => {
          messageInput.onSelectItem(item);
        },
      },
      ...emojiDataProvider,
      ...commandsDataProvider(getCommands()),
    }),
    [],
  );

  async function handleUpload(files: FileList) {
    const fileList = Array.from(files);
    let error = false;

    for (const file of fileList) {
      const isImageType = file.type.includes('image/');
      const isImageExtension = file.name.match(/.(jpg|jpeg|png|gif)$/i);

      if (!isImageType || !isImageExtension) {
        notify.error({ message: t('errors:imagesOnly') })
        error = true;
      }
    }

    if (error) {
      Sentry.captureMessage('An attempt was made to upload a non-image file', {
        user: {
          name: currentUser.full_name,
          email: currentUser.email_address,
          client_id: currentUser.client_id,
        }
      });
    }

    if (!error) {
      messageInput.uploadNewFiles(files);
    }
  }

  return (
    <div className={classNames('str-chat__input-flat', classes.root)}>
      <ImageDropzone
        accept={channelContext.acceptedFiles}
        multiple={channelContext.multipleUploads}
        disabled={
          channelContext.maxNumberOfFiles !== undefined &&
          messageInput.numberOfUploads >= channelContext.maxNumberOfFiles
        }
        handleFiles={messageInput.uploadNewFiles}>
        <div className="str-chat__input-flat-wrapper">
          <EmojiPicker {...messageInput} />
          <div className="str-chat__input-flat--textarea-wrapper">
            <UploadsPreview {...messageInput} />
            <ChatAutoComplete
              commands={messageInput.getCommands()}
              innerRef={messageInput.textareaRef}
              handleSubmit={messageInput.handleSubmit}
              onSelectItem={messageInput.onSelectItem}
              onChange={messageInput.handleChange}
              value={messageInput.text}
              rows={1}
              maxRows={props.maxRows}
              placeholder={t('discussions:typeMessage')}
              onPaste={messageInput.onPaste}
              triggers={getTriggers()}
              grow={props.grow}
              disabled={props.disabled}
              additionalTextareaProps={props.additionalTextareaProps}
            />
            <Tooltip title={t('discussions:openEmojiPicker')}>
              <span
                className="str-chat__input-flat-emojiselect"
                onClick={messageInput.openEmojiPicker}>
                <InsertEmoticonIcon />
              </span>
            </Tooltip>
            <FileUploadButton
              multiple={channelContext.multipleUploads}
              disabled={
                channelContext.maxNumberOfFiles !== undefined &&
                messageInput.numberOfUploads >= channelContext.maxNumberOfFiles
              }
              accepts={ACCEPTED_FILES}
              handleFiles={handleUpload}>
              <Tooltip title={t('discussions:attachFiles')}>
                <InsertPhotoIcon />
              </Tooltip>
            </FileUploadButton>
          </div>
          <SendButton sendMessage={messageInput.handleSubmit} />
        </div>
      </ImageDropzone>
    </div>
  );
};

export default memo(MessageInputCustom);
